<script setup lang="ts">
  import {
    useUnsavedChangesModelValue,
    type UnsavedChangesOptions
  } from '@gat-ship/lib/router'
  import { globalUtils, useAppStateProvider, useDisplay } from '@gat-ship/utils'
  import type { IconValues } from '@gat-ship/icons'
  import { GatIcon } from '../../gat-icon'
  import { GatButton, type GatButtonProps } from '../../gat-button'
  import { ScrollArea } from '../../ui/scroll-area'
  import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger,
    type SheetVariants
  } from '../../ui/sheet'
  import AppDrawerContent from './app-drawer-content.vue'
  import { useWindowSize } from '@vueuse/core'
  import {
    computed,
    nextTick,
    onMounted,
    onUpdated,
    reactive,
    ref,
    useSlots,
    useTemplateRef,
    watch,
    type HTMLAttributes,
    type StyleValue
  } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'

  export interface AppDrawerProps {
    class?: HTMLAttributes['class']
    title: string
    description?: string | ((...args: any[]) => string)
    icon?: IconValues
    buttons?: {
      ok?: GatButtonProps | false
      cancel?: GatButtonProps | false
    }
    side?: SheetVariants['side']
    maxWidth?: number
    loading?: boolean
    closeOnOutsideClick?: boolean
    noFooter?: boolean
    hideCloseButton?: boolean
  }

  defineOptions({ inheritAttrs: false })

  const props = withDefaults(defineProps<AppDrawerProps>(), {
    side: 'right',
    maxWidth: 400
  })

  const { isLoading } = useAppStateProvider(props, true)

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:save')
  })

  const isOpen = defineModel('open', { default: false })

  const slots = useSlots()

  const emit = defineEmits<{
    (e: 'update:open', openState: boolean): void
    (e: 'click:save'): void
    (e: 'click:cancel'): void
  }>()

  const unsavedChangesOptions = reactive<UnsavedChangesOptions>({
    preventTabClose: true,
    preventNavigationWhenOpen: true
  })

  const {
    setState,
    hasState,
    hasChanges: hasUnsavedChanges
  } = useUnsavedChangesModelValue(isOpen, unsavedChangesOptions)

  const okButtonProps = computed((): GatButtonProps | undefined => {
    const defaults = {
      label: 'Save Changes',
      text: 'Save Changes'
    }
    if (props.buttons?.ok === false) {
      return undefined
    }
    if (props.buttons?.ok) {
      return {
        ...defaults,
        ...props.buttons.ok
      }
    }
    if (hasEvent.clickSave) {
      return {
        ...defaults,
        ...props.buttons?.ok
      }
    }
    return undefined
  })
  const cancelButtonProps = computed((): GatButtonProps | undefined => {
    const defaults = {
      label: 'Cancel',
      text: 'Cancel',
      variant: 'ghost' as const
    }
    if (props.buttons?.cancel === false) {
      return undefined
    }
    if (props.buttons?.cancel !== undefined) {
      return {
        ...defaults,
        ...props.buttons.cancel
      }
    }
    return {
      ...defaults
    }
  })

  async function cancelClicked(event: MouseEvent | undefined) {
    // let canClose = unsavedChanges.value ? await navigationStateService.promptLeaveAlert() : true
    // if (!canClose) {
    //   return
    // }
    if (hasEvent.clickCancel) {
      emit('click:cancel')
    } else if (cancelButtonProps.value?.onClick) {
      if (Array.isArray(cancelButtonProps.value?.onClick)) {
        cancelButtonProps.value?.onClick.forEach((e) => e())
      } else {
        cancelButtonProps.value.onClick(event as MouseEvent)
      }
    } else {
      isOpen.value = false
    }
  }

  const okButtonIsDisabled = computed(() => {
    if (props.buttons?.ok && props.buttons.ok.disabled) {
      return props.buttons.ok.disabled
    }
    if (hasState.value) {
      return !hasUnsavedChanges.value
    }

    return false
  })

  function setUnsavedChangesOptions(options: Partial<UnsavedChangesOptions>) {
    console.log('setUnsavedChangesOptions', options)
    if (options.preventTabClose !== undefined) {
      unsavedChangesOptions.preventTabClose = options.preventTabClose
    }
    if (options.preventNavigationWhenOpen !== undefined) {
      unsavedChangesOptions.preventNavigationWhenOpen =
        options.preventNavigationWhenOpen
    }
  }

  const errorMessage = ref<string | undefined>(undefined)

  function handleOkClicked() {
    if (okButtonProps.value?.onClick) {
      okButtonProps.value.onClick()
    }
    emit('click:save')
  }

  defineExpose({
    setDrawerDataState: setState,
    setUnsavedChangesOptions,
    setErrorMessage: (message: string) => {
      errorMessage.value = message
    },
    clearErrorMessage: () => {
      errorMessage.value = undefined
    }
  })

  const { zIndex } = useDialogZIndex(isOpen)
</script>
<template>
  <Sheet v-model:open="isOpen">
    <SheetTrigger v-if="slots['trigger']" as-child>
      <slot name="trigger" />
    </SheetTrigger>
    <AppDrawerContent
      :overlay-z-index="zIndex"
      :class="props.class"
      :hide-close-button="isLoading || props.hideCloseButton"
      :close-on-outside-click="props.closeOnOutsideClick"
      :max-width="props.maxWidth"
      @click:close="cancelClicked"
      @escape-key-down="isOpen = false"
      @keydown.shift.enter.exact.prevent="handleOkClicked"
    >
      <div ref="header">
        <div class="flex w-full flex-col gap-2 p-5">
          <slot name="header">
            <div class="flex w-full shrink-0 items-center gap-2">
              <div class="flex w-full">
                <div class="flex flex-grow items-center text-base font-medium">
                  <slot name="title">
                    {{ title }}
                  </slot>
                </div>
                <div class="w-fit">
                  <slot name="actions" />
                </div>
              </div>
            </div>
            <SheetDescription
              v-if="description"
              class="text-soft text-wrap text-sm leading-none"
            >
              {{
                typeof description === 'function' ? description() : description
              }}
            </SheetDescription>
          </slot>
        </div>
        <!-- <separator /> -->
      </div>
      <slot />
      <div v-if="!props.noFooter" ref="footer" class="mt-auto">
        <separator />
        <div
          v-if="errorMessage"
          class="border-destructive bg-destructive/10 m-2 rounded-md border p-2 text-xs text-red-500"
        >
          {{ errorMessage }}
        </div>
        <div class="flex flex-row-reverse gap-4 p-5">
          <slot name="footer">
            <gat-button
              v-if="okButtonProps"
              v-bind="okButtonProps"
              :label="okButtonProps.label"
              class="grow"
              :disabled="okButtonIsDisabled"
              :data-testid="testIds.common.dialog.saveButton"
              size="default"
              @click="[emit('click:save'), okButtonProps.onClick]"
            />
            <gat-button
              v-if="cancelButtonProps"
              v-bind="cancelButtonProps"
              class="text-soft grow"
              variant="outline"
              size="default"
              @click="($event) => cancelClicked($event)"
            />
          </slot>
        </div>
      </div>
    </AppDrawerContent>
  </Sheet>
</template>
